// 📄 매매 계약서 PDF 생성 유틸리티
// 중고 디지털 기기 매입 프로세스에 특화된 계약서 PDF 생성

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Application } from '../pages/admin/types/application';

// 📋 계약 조항 정의 (ContractPage와 동일)
const CONTRACT_TERMS = [
  {
    id: 'ownership',
    title: '소유권 확인',
    content: '판매하는 기기는 본인 소유이며, 분실/도난 신고된 기기가 아닙니다.',
    required: true,
  },
  {
    id: 'lost_stolen_responsibility',
    title: '분실/도난 책임',
    content: '분실/도난 기기가 아님을 확인 후 매도 하였으며, 추후에 분실/도난 사유가 발생할 경우 모든 민·형사 책임을 지는 것에 동의 합니다.',
    required: true,
  },
  {
    id: 'unpaid_responsibility',
    title: '미납/연체 책임',
    content: '미납/연체/직권해지 등 사유로 추후에 타인이 이용시 문제가 발생할 경우 모든 민·형사 책임을 지는 것에 동의 합니다.',
    required: true,
  },
  {
    id: 'no_refund_agreement',
    title: '거래 취소 불가',
    content: '매도 후 단순 변심 및 기타 사유로 거래 취소 및 환불이 될 수 없음에 동의 합니다.',
    required: true,
  },
  {
    id: 'final_sale',
    title: '최종 판매 확정',
    content: '계약 체결 후 판매 취소나 기기 반환을 요구하지 않습니다.',
    required: true,
  },
  {
    id: 'payment_terms',
    title: '입금 조건',
    content: '영업일 기준 2-3일 내 지정 계좌로 입금받는 것에 동의합니다.',
    required: false,
  },
];

interface ContractPdfOptions {
  application: Application;
  signatureData: string;
  contractTermsAgreed: Record<string, boolean>;
  contractDate: Date;
  deviceId?: string; // 특정 기기 계약 시 사용
}

/**
 * 📄 매매 계약서 PDF 생성 함수 (HTML to PDF 방식으로 한글 지원)
 * @param options 계약서 생성에 필요한 데이터
 * @returns PDF blob 데이터
 */
export const generateContractPDF = async (options: ContractPdfOptions): Promise<Blob> => {
  console.log('📄 [generateContractPDF] PDF 생성 시작', options);
  
  const { application, signatureData, contractTermsAgreed, contractDate, deviceId } = options;

  try {
    // HTML to Canvas 방식으로 한글 지원
    console.log('📄 [generateContractPDF] HTML to Canvas 방식으로 PDF 생성 중...');
    
    // HTML 계약서 생성
    const contractHTML = generateContractHTML({
      application,
      signatureData,
      contractTermsAgreed,
      contractDate,
      deviceId,
    });

    // 임시 div 생성 (화면에 보이지 않게)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = contractHTML;
    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0px';
    tempDiv.style.width = '800px'; // 고정 폭
    tempDiv.style.height = 'auto';
    tempDiv.style.backgroundColor = 'white';
    tempDiv.style.fontFamily = '"Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", sans-serif';
    tempDiv.style.fontSize = '12px';
    tempDiv.style.lineHeight = '1.5';
    tempDiv.style.color = 'black';
    tempDiv.style.zIndex = '-1000';
    tempDiv.style.overflow = 'visible';
    
    document.body.appendChild(tempDiv);
    
    // 폰트 로딩 대기
    await new Promise(resolve => setTimeout(resolve, 500));

    console.log('📄 [generateContractPDF] HTML을 캔버스로 변환 중...');
    
    // HTML을 캔버스로 변환
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: tempDiv.scrollWidth,
      height: tempDiv.scrollHeight,
    });

    console.log('📄 [generateContractPDF] 캔버스 생성 완료:', canvas.width, 'x', canvas.height);

    // PDF 생성
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgData = canvas.toDataURL('image/png', 0.95);
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // A4 크기에 맞게 스케일링
    const ratio = Math.min(pdfWidth / (imgWidth * 0.264583), pdfHeight / (imgHeight * 0.264583));
    const scaledWidth = (imgWidth * 0.264583) * ratio;
    const scaledHeight = (imgHeight * 0.264583) * ratio;
    
    console.log('📄 [generateContractPDF] 이미지를 PDF에 추가 중...');
    
    // 이미지가 한 페이지를 넘는 경우 여러 페이지로 분할
    if (scaledHeight > pdfHeight) {
      const pageCount = Math.ceil(scaledHeight / pdfHeight);
      const pageHeight = imgHeight / pageCount;
      
      for (let i = 0; i < pageCount; i++) {
        if (i > 0) pdf.addPage();
        
        // 각 페이지별로 이미지 잘라서 추가
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        pageCanvas.width = imgWidth;
        pageCanvas.height = pageHeight;
        
        if (pageCtx) {
          pageCtx.drawImage(canvas, 0, pageHeight * i, imgWidth, pageHeight, 0, 0, imgWidth, pageHeight);
          const pageImgData = pageCanvas.toDataURL('image/png', 0.95);
          pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        }
      }
    } else {
      pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
    }

    // 임시 div 제거
    document.body.removeChild(tempDiv);

    console.log('📄 [generateContractPDF] PDF 생성 완료');
    const blob = pdf.output('blob');
    console.log('📄 [generateContractPDF] Blob 크기:', blob.size, 'bytes');
    
    return blob;
  } catch (error) {
    console.error('📄 [generateContractPDF] PDF 생성 실패:', error);
    throw error;
  }
};

/**
 * 📄 HTML 계약서 생성 함수
 * @param options 계약서 생성에 필요한 데이터
 * @returns HTML 문자열
 */
const generateContractHTML = (options: ContractPdfOptions): string => {
  const { application, signatureData, contractTermsAgreed, contractDate, deviceId } = options;

  const devices = (application as any).devices || [];
  const selectedDevice = deviceId ? devices.find((d: any) => d.id === deviceId) : null;

  const displayModel = selectedDevice
    ? (selectedDevice.deviceModel || '기기정보없음')
    : getDeviceModelsText(devices);

  const displayPriceNumber = selectedDevice
    ? (selectedDevice.final_price ?? selectedDevice.price_offer ?? 0)
    : ((application as any).total_final_price || (application as any).total_price_offer || (application as any).final_price || (application as any).price_offer || 0);
  const displayPrice = Number(displayPriceNumber).toLocaleString();

  return `
    <div style="font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif; width: 800px; margin: 0; padding: 40px; background: white; box-sizing: border-box;">
      <!-- 헤더 -->
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="font-size: 24px; font-weight: bold; margin: 0 0 20px 0; color: #000; letter-spacing: 1px;">
          중고휴대기기 매매 계약서
        </h1>
        <p style="font-size: 12px; color: #333; margin: 5px 0; line-height: 1.4;">
          판매하실 휴대기기의 매입거래를 정식으로 인정하는 필수 첨부 서류 입니다.
        </p>
        <p style="font-size: 12px; color: #333; margin: 5px 0; line-height: 1.4;">
          아래 내용을 정확히 필독하신 후 고객님께서 자필로 직접 작성해주세요.
        </p>
      </div>

      <!-- 매입 내용 -->
      <div style="margin-bottom: 25px;">
        <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #000;">
          [매입 내용]
        </h3>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="border: 1px solid #000; padding: 8px; background-color: #f0f0f0; font-weight: bold; width: 20%; text-align: center; font-size: 12px;">매각일자</td>
            <td style="border: 1px solid #000; padding: 8px; width: 30%; text-align: center; font-size: 12px;">${formatDateKorean(contractDate)}</td>
            <td style="border: 1px solid #000; padding: 8px; background-color: #f0f0f0; font-weight: bold; width: 20%; text-align: center; font-size: 12px;">판매자명</td>
            <td style="border: 1px solid #000; padding: 8px; width: 30%; text-align: center; font-size: 12px;">${application.user_name}</td>
          </tr>
          <tr>
            <td style="border: 1px solid #000; padding: 8px; background-color: #f0f0f0; font-weight: bold; text-align: center; font-size: 12px;">모델명</td>
            <td style="border: 1px solid #000; padding: 8px; text-align: center; font-size: 12px;">${displayModel}</td>
            <td style="border: 1px solid #000; padding: 8px; background-color: #f0f0f0; font-weight: bold; text-align: center; font-size: 12px;">연락처</td>
            <td style="border: 1px solid #000; padding: 8px; text-align: center; font-size: 12px;">${application.phone_number}</td>
          </tr>
          <tr>
            <td style="border: 1px solid #000; padding: 8px; background-color: #f0f0f0; font-weight: bold; text-align: center; font-size: 12px;">판매금액</td>
            <td colspan="3" style="border: 1px solid #000; padding: 8px; font-weight: bold; text-align: center; font-size: 14px;">
              ${displayPrice}원
            </td>
          </tr>
        </table>

        <!-- 판매자 계좌 정보 -->
        <div style="margin-bottom: 15px; font-size: 12px; line-height: 1.5;">
          <p style="margin: 3px 0;"><strong>은행명:</strong> ${application.bank_name || ''}</p>
          <p style="margin: 3px 0;"><strong>예금주:</strong> ${application.account_holder || application.user_name}</p>
          <p style="margin: 3px 0;"><strong>계좌번호:</strong> ${application.bank_account || ''}</p>
        </div>
      </div>

      <!-- 계약 조항 -->
      <div style="margin-bottom: 25px;">
        <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #000;">
          계약 조항
        </h3>
        
        ${CONTRACT_TERMS.map((term, index) => {
          const isAgreed = contractTermsAgreed[term.id];
          // 체크된 조항만 표시
          if (!isAgreed) return '';
          
          return `
            <div style="margin-bottom: 12px; padding: 8px; border: 1px solid #ccc; border-radius: 4px; background-color: #f9f9f9;">
              <p style="margin: 0 0 5px 0; font-weight: bold; font-size: 12px; color: #000;">
                ☑ ${index + 1}. ${term.title} ${term.required ? '(필수)' : ''}
              </p>
              <p style="margin: 0; padding-left: 15px; color: #333; line-height: 1.5; font-size: 11px;">
                ${term.content}
              </p>
            </div>
          `;
        }).filter(Boolean).join('')}
      </div>

      <!-- 서명 -->
      <div style="margin-bottom: 25px;">
        <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #000;">
          서명
        </h3>
        
        <div style="text-align: center; margin-bottom: 15px; min-height: 100px; border: 1px solid #ccc; padding: 10px; background-color: #fafafa;">
          ${signatureData ? `
            <img src="${signatureData}" style="max-width: 250px; max-height: 80px; display: block; margin: 0 auto;" alt="서명" />
          ` : `
            <div style="padding: 30px; color: #999; font-size: 12px;">
              서명: ________________
            </div>
          `}
        </div>
        
        <div style="display: flex; justify-content: space-between; font-size: 12px;">
          <p style="margin: 0;"><strong>서명일:</strong> ${formatDate(contractDate)}</p>
          <p style="margin: 0;"><strong>서명자:</strong> ${application.user_name}</p>
        </div>
      </div>

      <!-- 구매자 정보 -->
      <div style="margin-bottom: 20px;">
        <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 10px 0; color: #000;">
          구매자
        </h3>
        <p style="margin: 3px 0; font-weight: bold; font-size: 12px;">주식회사 다원트레이드</p>
        <p style="margin: 3px 0; font-size: 12px;">사업자번호: 750-81-00782</p>
      </div>

      <!-- 푸터 -->
      <div style="text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #ccc;">
        <p style="font-size: 9px; color: #666; margin: 3px 0;">
          본 계약서는 전자서명법에 따라 법적 효력을 가집니다.
        </p>
        <p style="font-size: 9px; color: #666; margin: 3px 0;">
          계약서 생성일시: ${new Date().toLocaleString('ko-KR')}
        </p>
      </div>
    </div>
  `;
};

/**
 * 📄 HTML 요소를 PDF로 변환하는 함수 (고급 레이아웃용)
 * @param elementId HTML 요소 ID
 * @param filename PDF 파일명
 */
export const generatePDFFromHTML = async (elementId: string, filename: string): Promise<Blob> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with ID '${elementId}' not found`);
  }

  // HTML을 캔버스로 변환
  const canvas = await html2canvas(element, {
    scale: 2, // 고해상도
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  
  // 이미지 비율에 맞춰 PDF 크기 조정
  const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  const scaledWidth = imgWidth * ratio;
  const scaledHeight = imgHeight * ratio;
  
  pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
  
  return pdf.output('blob');
};

/**
 * 📅 날짜 포맷팅 함수 (yyyy-mm-dd 형식)
 */
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * 📅 한국어 날짜 포맷팅 함수 (yyyy년 mm월 dd일 형식)
 */
const formatDateKorean = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}년 ${month}월 ${day}일`;
};

/**
 * 📱 기기 모델명 텍스트 생성
 */
const getDeviceModelsText = (devices: any[]): string => {
  if (!devices || devices.length === 0) return '기기정보없음';
  
  if (devices.length === 1) {
    return devices[0].deviceModel;
  }
  
  return `${devices[0].deviceModel} 외 ${devices.length - 1}건`;
};

/**
 * 💾 PDF 파일 다운로드 함수
 */
export const downloadPDF = (blob: Blob, filename: string) => {
  console.log('💾 [downloadPDF] 다운로드 시작');
  console.log('💾 [downloadPDF] Blob 크기:', blob.size, 'bytes');
  console.log('💾 [downloadPDF] 파일명:', filename);
  
  try {
    const url = URL.createObjectURL(blob);
    console.log('💾 [downloadPDF] Object URL 생성:', url);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    console.log('💾 [downloadPDF] 링크 클릭 실행');
    link.click();
    
    // 약간의 지연 후 정리
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('💾 [downloadPDF] 정리 완료');
    }, 100);
    
  } catch (error) {
    console.error('❌ [downloadPDF] 다운로드 실패:', error);
    throw error;
  }
};

/**
 * 📤 PDF를 서버에 업로드하는 함수 (추후 구현)
 */
export const uploadContractPDF = async (
  blob: Blob, 
  applicationId: string, 
  filename: string
): Promise<string> => {
  // TODO: Firebase Storage 또는 다른 클라우드 스토리지에 업로드
  // 현재는 로컬 다운로드만 지원
  console.log('PDF 업로드 기능은 추후 구현 예정:', { applicationId, filename });
  return '';
};
